import React, { useMemo, useState } from 'react';
import { Building2, ExternalLink, X } from 'lucide-react';
import Carousel from '../../ui/Carousel';
import {
  ArchitectureProductData,
  useFeatureLicenses,
  useProductFeatureDetail,
} from '../../../hooks/gtm-architecture-hooks';
import { DataError, SkeletonCards } from './architectureSections';

/**
 * The products section of an architecture page.
 *
 * A go-to-market motion sells the features of several products, so this renders the product
 * master — components with their features, licenses, appliances and versions — rather than
 * anything stored on the motion itself. Solution category badges filter the whole carousel;
 * licenses filter the features *within* one card. A feature's licenses and success criteria are
 * the only things the master doesn't carry, so they load when a feature card is expanded.
 */

export interface ArchitectureProductsProps extends ArchitectureProductData {
  onRetry: () => void;
}

const CATEGORY_COLORS = [
  'text-[#34d399]',
  'text-[#60a5fa]',
  'text-[#22d3ee]',
  'text-[#fbbf24]',
  'text-[#a78bfa]',
];

const licenseLabel = (l: any): string => l?.product_license?.name || l?.name || l?.value || String(l);

const ArchitectureProducts: React.FC<ArchitectureProductsProps> = ({
  products,
  productDetails,
  productAppliances,
  productVersions,
  featureDetails,
  isLoading,
  isError,
  onRetry,
}) => {
  const [categoryFilter, setCategoryFilter] = useState<string | null>(null);
  const [featureLicenseFilter, setFeatureLicenseFilter] = useState<Record<number, string | null>>({});
  const [componentsModal, setComponentsModal] = useState<{
    productName: string;
    compGroups: any[];
  } | null>(null);
  const [featureModal, setFeatureModal] = useState<{
    id?: number;
    name: string;
    fdetail: any;
    licenses: any[];
  } | null>(null);

  // The product whose license filter is on. The cached master is a list response, so its features
  // carry no licenses — the mapping the filter needs is looked up on the click that turns it on,
  // and only for the features that product actually shows.
  const licenseFilterProductId = Number(
    Object.keys(featureLicenseFilter).find((id) => featureLicenseFilter[Number(id)]) ?? NaN
  );
  const licenseFilterFeatureIds = useMemo(() => {
    if (Number.isNaN(licenseFilterProductId)) return [];
    const product = products.find((p) => p.id === licenseFilterProductId);
    return (product?.features || [])
      .map((f: any) => f?.id)
      .filter((id: any): id is number => typeof id === 'number');
  }, [products, licenseFilterProductId]);
  const licenseQuery = useFeatureLicenses(licenseFilterFeatureIds);

  // The open feature's own row — success criteria and licenses aren't on the product master.
  const featureQuery = useProductFeatureDetail(featureModal?.id);
  const openFeature = featureModal ? featureModal.fdetail || featureQuery.data : null;
  const openFeatureCriteria = featureQuery.data?.product_feature_success_criteria || [];
  const openFeatureLicenses = featureModal?.licenses?.length
    ? featureModal.licenses
    : featureQuery.data?.license || [];

  // Solution category badges come from each product's offer type and architecture tags.
  const productMeta = products.map((product) => {
    const d = productDetails[product.id];
    const categories = [
      d?.offer_type ? String(d.offer_type).replace(/_/g, ' ') : null,
      ...(Array.isArray(d?.architecture) ? d.architecture : []).map(
        (a: any) => a.name || a.value || a
      ),
    ].filter(Boolean) as string[];
    return { categories };
  });
  const allCategories = Array.from(
    new Set(([] as string[]).concat(...productMeta.map((m) => m.categories)))
  );
  const filteredProducts = products.filter((_, i) =>
    categoryFilter ? productMeta[i].categories.includes(categoryFilter) : true
  );

  if (isLoading) return <SkeletonCards count={3} />;
  if (isError) return <DataError label="products" onRetry={onRetry} />;
  if (products.length === 0) {
    return <div className="text-center text-[#6b8299] py-12">No data available.</div>;
  }

  return (
    <>
      {allCategories.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mb-8">
          <span className="text-[#6b8299] text-xs font-semibold mr-1">
            Filter by Solution Category:
          </span>
          {allCategories.map((c) => (
            <button
              key={`catf-${c}`}
              type="button"
              onClick={() => setCategoryFilter((f) => (f === c ? null : c))}
              className={`px-3 py-1.5 rounded-full text-xs font-medium capitalize border transition-colors ${
                categoryFilter === c
                  ? 'bg-[#049fd9] border-[#049fd9] text-white'
                  : 'bg-transparent border-white/15 text-[#8eafc8] hover:border-[#049fd9]/50 hover:text-white'
              }`}
            >
              {c}
            </button>
          ))}
          {categoryFilter && (
            <button
              type="button"
              onClick={() => setCategoryFilter(null)}
              className="px-3 py-1.5 rounded-full text-xs font-medium text-[#6b8299] hover:text-white transition-colors"
            >
              Clear filter ✕
            </button>
          )}
        </div>
      )}

      {filteredProducts.length === 0 ? (
        <div className="text-center text-[#6b8299] py-12">
          No products match the selected category.
        </div>
      ) : (
        <Carousel
          theme="dark"
          ariaLabel="Products"
          itemClassName="w-[92%] sm:w-[85%] lg:w-[80%]"
          snapAlign="center"
          showDots={false}
          showProgressBar
        >
          {filteredProducts.map((product, i) => {
            const productId = product.id;
            const detail = productDetails[productId];
            const appliances = productAppliances[productId] || detail?.appliance || [];
            const versions = productVersions[productId] || detail?.version || [];
            const features = product.features?.length ? product.features : [];
            const compGroups = (detail?.component || []).filter((c: any) => c.product_feature?.length);
            const topLevelFeatures: any[] = Array.isArray(detail?.feature)
              ? detail.feature
              : Array.isArray(detail?.product_feature)
              ? detail.product_feature
              : [];
            const totalFeatures = topLevelFeatures.length || features.length;
            const prodArchitecture = Array.isArray(detail?.architecture) ? detail.architecture : [];
            const prodLicenses = Array.isArray(detail?.license) ? detail.license : [];
            const activeLicense = featureLicenseFilter[productId] || null;
            const categoryColor = CATEGORY_COLORS[i % CATEGORY_COLORS.length];

            // Licenses per feature: whatever the payload happens to carry, plus the per-feature
            // lookup for the product being filtered.
            const featureLicenseIndex: Record<number, any[]> = {};
            const licenseSources = [
              topLevelFeatures,
              ...(detail?.component || []).map((c: any) => c.product_feature || []),
            ];
            licenseSources.forEach((source: any[]) => {
              source.forEach((f: any) => {
                if (!featureLicenseIndex[f?.id] && Array.isArray(f?.license) && f.license.length) {
                  featureLicenseIndex[f.id] = f.license;
                }
              });
            });
            if (productId === licenseFilterProductId && licenseQuery.data) {
              Object.entries(licenseQuery.data).forEach(([fid, licenses]) => {
                if (Array.isArray(licenses) && licenses.length) {
                  featureLicenseIndex[Number(fid)] = licenses;
                }
              });
            }
            // Until the licenses land, filtering would hide every feature — show them all and let
            // the filter narrow the list once it can.
            const canFilterByLicense = Object.keys(featureLicenseIndex).length > 0;
            const featureHasLicense = (fid: number) =>
              !activeLicense ||
              !canFilterByLicense ||
              (featureLicenseIndex[fid] || []).some((l: any) => licenseLabel(l) === activeLicense);

            const links = [detail?.sales_url && { label: 'Sales', href: detail.sales_url }].filter(
              Boolean
            ) as { label: string; href: string }[];

            return (
              <div
                key={productId}
                className="h-full bg-[#0f2035] rounded-2xl border border-white/10 hover:border-white/20 transition-all overflow-hidden"
              >
                <div className="p-6 sm:p-8 border-b border-white/5 flex flex-col lg:flex-row lg:items-start gap-5">
                  <div className="w-14 h-14 rounded-xl bg-[#1a2d42] flex items-center justify-center shrink-0">
                    <Building2 className="w-6 h-6 text-[#8eafc8]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 flex-wrap mb-1.5">
                      <h3 className="text-white font-bold text-xl">{product.name}</h3>
                      {product.category && (
                        <span
                          className={`text-[11px] font-bold tracking-wider px-2.5 py-1 rounded-full bg-white/5 ${categoryColor}`}
                        >
                          {product.category}
                        </span>
                      )}
                    </div>
                    {product.description && (
                      <p className="text-[#8eafc8] text-sm leading-relaxed max-w-3xl">
                        {product.description}
                      </p>
                    )}
                    {(detail?.offer_type || prodArchitecture.length > 0) && (
                      <div className="flex flex-wrap items-center gap-1.5 mt-3">
                        <span className="text-[#6b8299] text-[10px] font-bold tracking-wide uppercase">
                          Solution Category
                        </span>
                        {detail?.offer_type &&
                          (() => {
                            const label = String(detail.offer_type).replace(/_/g, ' ');
                            return (
                              <button
                                type="button"
                                onClick={() => setCategoryFilter((f) => (f === label ? null : label))}
                                className={`px-2 py-0.5 rounded text-xs border capitalize transition-colors ${
                                  categoryFilter === label
                                    ? 'bg-[#049fd9]/25 border-[#049fd9]/60 text-white'
                                    : 'bg-[#1a2d42] border-white/5 text-[#8eafc8] hover:border-[#049fd9]/40 hover:text-white'
                                }`}
                              >
                                {label}
                              </button>
                            );
                          })()}
                        {prodArchitecture.map((a: any, ai: number) => {
                          const label = a.name || a.value || a;
                          return (
                            <button
                              key={`arch${ai}`}
                              type="button"
                              onClick={() => setCategoryFilter((f) => (f === label ? null : label))}
                              className={`px-2 py-0.5 rounded text-xs border transition-colors ${
                                categoryFilter === label
                                  ? 'bg-[#049fd9]/25 border-[#049fd9]/60 text-white'
                                  : 'bg-[#1a2d42] border-white/5 text-[#8eafc8] hover:border-[#049fd9]/40 hover:text-white'
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 p-6 sm:p-8">
                  <div className="lg:col-span-2 space-y-6">
                    {compGroups.length > 0 &&
                      (() => {
                        const visibleGroups = compGroups
                          .map((c: any) => ({
                            ...c,
                            product_feature: c.product_feature.filter((f: any) =>
                              featureHasLicense(f.id)
                            ),
                          }))
                          .filter((c: any) => !activeLicense || c.product_feature.length > 0);
                        return (
                          <div>
                            <div className="text-[#8eafc8] text-xs font-bold mb-3 tracking-wide flex flex-wrap items-center gap-1.5">
                              <span>FEATURES BY COMPONENT ({totalFeatures})</span>
                              {activeLicense && (
                                <span className="text-[#34d399] font-normal normal-case tracking-normal">
                                  · filtered by {activeLicense}
                                </span>
                              )}
                            </div>
                            {visibleGroups.length === 0 ? (
                              <p className="text-[#4a5d70] text-xs">
                                No features require the &quot;{activeLicense}&quot; license.
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                {visibleGroups.slice(0, 4).map((c: any) => (
                                  <div
                                    key={c.id}
                                    className="bg-[#0b1929] border border-white/5 rounded-xl p-4"
                                  >
                                    <div className="text-sm text-[#d0e4f4] font-semibold leading-tight mb-2">
                                      {c.name}{' '}
                                      <span className="text-[#3a5068] font-normal">
                                        ({c.product_feature.length})
                                      </span>
                                    </div>
                                    <div className="space-y-1">
                                      {c.product_feature.slice(0, 5).map((f: any) => (
                                        <div key={f.id} className="text-xs text-[#c9d8e6]">
                                          • {f.name}
                                        </div>
                                      ))}
                                      {c.product_feature.length > 5 && (
                                        <div className="text-[#3a5068] text-xs ml-3">
                                          +{c.product_feature.length - 5} more
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            {visibleGroups.length > 4 && (
                              <button
                                type="button"
                                onClick={() =>
                                  setComponentsModal({
                                    productName: product.name,
                                    compGroups: visibleGroups,
                                  })
                                }
                                className="text-[#049fd9] text-xs font-semibold mt-3 hover:text-[#16bdeb] transition-colors"
                              >
                                {`Show all ${visibleGroups.length} components`}
                              </button>
                            )}
                          </div>
                        );
                      })()}

                    {compGroups.length === 0 &&
                      features.length > 0 &&
                      (() => {
                        const visibleFeatures = features.filter((f: any) => featureHasLicense(f.id));
                        return (
                          <div>
                            <div className="text-[#8eafc8] text-xs font-bold mb-3 tracking-wide flex flex-wrap items-center gap-1.5">
                              <span>KEY FEATURES ({features.length})</span>
                              {activeLicense && (
                                <span className="text-[#34d399] font-normal normal-case tracking-normal">
                                  · filtered by {activeLicense}
                                </span>
                              )}
                            </div>
                            {visibleFeatures.length === 0 ? (
                              <p className="text-[#4a5d70] text-xs">
                                No features require the &quot;{activeLicense}&quot; license.
                              </p>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {visibleFeatures.slice(0, 6).map((feat: any, fi: number) => {
                                  const fid = feat.id;
                                  const fdetail = featureDetails[fid];
                                  const licenses = featureLicenseIndex[fid] || [];
                                  const summary =
                                    fdetail?.why_its_important || fdetail?.description || '';
                                  // Criteria and licenses arrive with the modal's own request, so
                                  // the affordance follows what the master already tells us.
                                  const hasMore = !!(
                                    fid != null &&
                                    (fdetail?.how_to_sell || summary.length > 90 || licenses.length)
                                  );

                                  return (
                                    <div
                                      key={fid ?? fi}
                                      className="bg-[#0b1929] border border-white/5 rounded-xl p-4"
                                    >
                                      <div className="flex items-start gap-2">
                                        <div
                                          className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${
                                            fi % 4 === 0
                                              ? 'bg-[#34d399]'
                                              : fi % 4 === 1
                                              ? 'bg-[#60a5fa]'
                                              : fi % 4 === 2
                                              ? 'bg-[#22d3ee]'
                                              : 'bg-[#fbbf24]'
                                          }`}
                                        />
                                        <div className="flex-1 min-w-0">
                                          <div className="text-xs text-[#d0e4f4] font-semibold leading-tight">
                                            {feat.name || feat}
                                          </div>
                                          {summary && (
                                            <div
                                              className="text-xs text-[#6b8299] mt-1 leading-relaxed"
                                              style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                              }}
                                            >
                                              {summary}
                                            </div>
                                          )}
                                          {hasMore && (
                                            <button
                                              type="button"
                                              onClick={() =>
                                                setFeatureModal({
                                                  id: fid,
                                                  name: feat.name || feat,
                                                  fdetail,
                                                  licenses,
                                                })
                                              }
                                              className="text-[#049fd9] text-xs font-semibold mt-1.5 hover:text-[#16bdeb] transition-colors"
                                            >
                                              Show more
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                            {visibleFeatures.length > 6 && (
                              <div className="text-[#3a5068] text-xs mt-3">
                                +{visibleFeatures.length - 6} more features
                              </div>
                            )}
                          </div>
                        );
                      })()}

                    {features.length === 0 && compGroups.length === 0 && product.specs?.length > 0 && (
                      <div>
                        <div className="text-[#8eafc8] text-xs font-bold mb-2 tracking-wide">SPECS</div>
                        <ul className="space-y-1">
                          {product.specs.map((spec: any, si: number) => (
                            <li key={si} className="text-xs text-[#6b8299]">
                              {spec}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {product.use_cases?.length > 0 && (
                      <div>
                        <div className="text-[#8eafc8] text-xs font-bold mb-2 tracking-wide">
                          USE CASES
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {product.use_cases.slice(0, 8).map((uc: any, ui: number) => (
                            <span
                              key={ui}
                              className="px-2.5 py-1 bg-[#1a2d42] rounded-full text-[#8eafc8] text-xs border border-white/5"
                            >
                              {uc}
                            </span>
                          ))}
                        </div>
                        {product.use_cases.length > 8 && (
                          <details className="mt-1.5">
                            <summary className="text-[#049fd9] text-xs font-semibold cursor-pointer hover:text-[#16bdeb] transition-colors list-none marker:hidden">
                              +{product.use_cases.length - 8} more
                            </summary>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {product.use_cases.slice(8).map((uc: any, ui: number) => (
                                <span
                                  key={`more${ui}`}
                                  className="px-2.5 py-1 bg-[#1a2d42] rounded-full text-[#8eafc8] text-xs border border-white/5"
                                >
                                  {uc}
                                </span>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="space-y-5 lg:border-l lg:border-white/5 lg:pl-8">
                    {prodLicenses.length > 0 && (
                      <div>
                        <div className="text-[#8eafc8] text-xs font-bold mb-1.5 tracking-wide">
                          LICENSES AVAILABLE
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {prodLicenses.map((l: any, li: number) => {
                            const label = licenseLabel(l);
                            return (
                              <button
                                key={`lic${li}`}
                                type="button"
                                onClick={() =>
                                  setFeatureLicenseFilter((m) => ({
                                    ...m,
                                    [productId]: m[productId] === label ? null : label,
                                  }))
                                }
                                className={`px-2 py-0.5 border text-xs rounded transition-colors ${
                                  activeLicense === label
                                    ? 'bg-[#10b981]/30 border-[#34d399]/70 text-white'
                                    : 'bg-[#10b981]/10 border-[#10b981]/30 text-[#6ee7b7] hover:bg-[#10b981]/20'
                                }`}
                              >
                                {label}
                              </button>
                            );
                          })}
                        </div>
                        <p className="text-[#4a5d70] text-[10px] mt-1.5 leading-relaxed">
                          {activeLicense && licenseQuery.isLoading
                            ? 'Looking up which features this license unlocks…'
                            : 'Click a license to filter the features it unlocks.'}
                        </p>
                      </div>
                    )}

                    {appliances.length > 0 && (
                      <div>
                        <div className="text-[#8eafc8] text-xs font-bold mb-1.5 tracking-wide">
                          APPLIANCES ({appliances.length})
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {appliances.slice(0, 6).map((app: any, ai: number) => (
                            <span
                              key={ai}
                              className="px-2 py-0.5 bg-[#1a2d42] rounded text-[#8eafc8] text-xs border border-white/5"
                            >
                              {app.name || app.model || app}
                            </span>
                          ))}
                        </div>
                        {appliances.length > 6 && (
                          <details className="mt-1.5">
                            <summary className="text-[#049fd9] text-xs font-semibold cursor-pointer hover:text-[#16bdeb] transition-colors list-none marker:hidden">
                              +{appliances.length - 6} more
                            </summary>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {appliances.slice(6).map((app: any, ai: number) => (
                                <span
                                  key={`more${ai}`}
                                  className="px-2 py-0.5 bg-[#1a2d42] rounded text-[#8eafc8] text-xs border border-white/5"
                                >
                                  {app.name || app.model || app}
                                </span>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    )}

                    {versions.length > 0 && (
                      <div>
                        <div className="text-[#8eafc8] text-xs font-bold mb-1.5 tracking-wide">
                          VERSIONS ({versions.length})
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {versions.slice(0, 6).map((v: any, vi: number) => (
                            <span
                              key={vi}
                              className="px-2 py-0.5 bg-[#1a2d42] rounded text-[#8eafc8] text-xs border border-white/5"
                            >
                              {v.name || v.version || `v${v.id}`}
                            </span>
                          ))}
                        </div>
                        {versions.length > 6 && (
                          <details className="mt-1.5">
                            <summary className="text-[#049fd9] text-xs font-semibold cursor-pointer hover:text-[#16bdeb] transition-colors list-none marker:hidden">
                              +{versions.length - 6} more
                            </summary>
                            <div className="flex flex-wrap gap-1.5 mt-1.5">
                              {versions.slice(6).map((v: any, vi: number) => (
                                <span
                                  key={`more${vi}`}
                                  className="px-2 py-0.5 bg-[#1a2d42] rounded text-[#8eafc8] text-xs border border-white/5"
                                >
                                  {v.name || v.version || `v${v.id}`}
                                </span>
                              ))}
                            </div>
                          </details>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {links.length > 0 && (
                  <div className="flex flex-wrap items-center gap-3 px-6 sm:px-8 py-5 border-t border-white/5 bg-white/[0.02]">
                    {links.map((l, li) => (
                      <a
                        key={li}
                        href={l.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="px-4 py-2 rounded-lg text-xs font-semibold border border-white/10 text-[#c9d8e6] hover:border-[#049fd9]/40 hover:text-white hover:bg-white/5 transition-colors inline-flex items-center gap-1.5 whitespace-nowrap"
                      >
                        {l.label} <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </Carousel>
      )}

      {componentsModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setComponentsModal(null)}
        >
          <div
            className="bg-[#0f2035] border border-white/10 rounded-2xl w-full max-w-5xl max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 flex items-center justify-between px-8 py-5 bg-[#0f2035] border-b border-white/10 z-10">
              <div>
                <div className="text-[#049fd9] text-xs font-bold tracking-wide mb-1">
                  FEATURES BY COMPONENT
                </div>
                <h3 className="text-white font-bold text-xl">{componentsModal.productName}</h3>
              </div>
              <button
                type="button"
                onClick={() => setComponentsModal(null)}
                className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-[#8eafc8]" />
              </button>
            </div>
            <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              {componentsModal.compGroups.map((c: any) => (
                <div key={c.id} className="bg-[#0b1929] border border-white/5 rounded-xl p-5">
                  <div className="text-sm text-[#d0e4f4] font-semibold mb-3">
                    {c.name}{' '}
                    <span className="text-[#3a5068] font-normal">({c.product_feature.length})</span>
                  </div>
                  <div className="space-y-3">
                    {c.product_feature.map((f: any) => (
                      <div key={f.id} className="pb-3 border-b border-white/5 last:border-0 last:pb-0">
                        <div className="text-sm text-[#c9d8e6] font-medium">• {f.name}</div>
                        {(f.description || f.short_description) && (
                          <div className="text-xs text-[#6b8299] ml-3 mt-1 leading-relaxed">
                            {f.description || f.short_description}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {featureModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
          onClick={() => setFeatureModal(null)}
        >
          <div
            className="bg-[#0f2035] border border-white/10 rounded-2xl w-full max-w-3xl max-h-[85vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 flex items-center justify-between px-8 py-5 bg-[#0f2035] border-b border-white/10 z-10">
              <h3 className="text-white font-bold text-xl">{featureModal.name}</h3>
              <button
                type="button"
                onClick={() => setFeatureModal(null)}
                className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 flex items-center justify-center transition-colors"
              >
                <X className="w-4 h-4 text-[#8eafc8]" />
              </button>
            </div>
            <div className="p-8 space-y-5">
              {(openFeature?.why_its_important || openFeature?.description) && (
                <p className="text-sm text-[#c9d8e6] leading-relaxed">
                  {openFeature.why_its_important || openFeature.description}
                </p>
              )}
              {openFeature?.how_to_sell && (
                <div className="bg-[#0b1929] border border-white/5 rounded-xl p-4">
                  <div className="text-[#049fd9] text-xs font-bold tracking-wide mb-1.5">
                    HOW TO SELL
                  </div>
                  <p className="text-sm text-[#4a7ba7] italic">💡 {openFeature.how_to_sell}</p>
                </div>
              )}
              {featureQuery.isLoading && (
                <div className="text-[#4a5d70] text-xs">Loading success criteria and licenses…</div>
              )}
              {openFeatureCriteria.length > 0 && (
                <div>
                  <div className="text-[#049fd9] text-xs font-bold tracking-wide mb-2">
                    SUCCESS CRITERIA
                  </div>
                  <div className="space-y-1.5">
                    {openFeatureCriteria.map((c: any, ci: number) => (
                      <div key={ci} className="text-sm text-[#34d399]">
                        ✓ {c.success_criteria}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {openFeatureLicenses.length > 0 && (
                <div>
                  <div className="text-[#049fd9] text-xs font-bold tracking-wide mb-2">LICENSES</div>
                  <div className="flex flex-wrap gap-2">
                    {openFeatureLicenses.map((l: any, li: number) => (
                      <span
                        key={li}
                        className="px-2.5 py-1 bg-[#049fd9]/10 border border-[#049fd9]/30 text-[#049fd9] text-xs rounded"
                      >
                        {l.product_license?.name || l.name || 'License'}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ArchitectureProducts;
